import { createFileRoute } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PAIRS, pipsBetween, pipValuePerLot } from "@/lib/mock-signals";
import { AlertTriangle, ShieldCheck, ShieldAlert } from "lucide-react";

export const Route = createFileRoute("/_authenticated/calculator")({
  component: RiskCalc,
});

const defaultEntries: Record<string, { entry: string; sl: string }> = {
  EURUSD: { entry: "1.08750", sl: "1.08500" },
  GBPUSD: { entry: "1.26540", sl: "1.26240" },
  USDJPY: { entry: "149.320", sl: "149.620" },
  AUDUSD: { entry: "0.66120", sl: "0.65880" },
  USDCAD: { entry: "1.35480", sl: "1.35780" },
  XAUUSD: { entry: "2034.50", sl: "2028.00" },
  GBPJPY: { entry: "188.940", sl: "189.440" },
  EURJPY: { entry: "162.410", sl: "162.110" },
};

function RiskCalc() {
  const [pair, setPair] = useState<string>("EURUSD");
  const [balance, setBalance] = useState("10000");
  const [risk, setRisk] = useState([1.5]);
  const [entry, setEntry] = useState(defaultEntries.EURUSD.entry);
  const [sl, setSl] = useState(defaultEntries.EURUSD.sl);

  const handlePair = (p: string) => {
    setPair(p);
    const d = defaultEntries[p];
    if (d) {
      setEntry(d.entry);
      setSl(d.sl);
    }
  };

  const result = useMemo(() => {
    const bal = parseFloat(balance) || 0;
    const ent = parseFloat(entry) || 0;
    const stop = parseFloat(sl) || 0;
    const rPct = risk[0];
    const dollarRisk = bal * (rPct / 100);
    const pips = pipsBetween(pair, ent, stop);
    const pipVal = pipValuePerLot(pair);
    const lots = pips > 0 && pipVal > 0 ? dollarRisk / (pips * pipVal) : 0;
    return {
      dollarRisk: dollarRisk.toFixed(2),
      lots: lots.toFixed(2),
      pipValue: (lots * pipVal).toFixed(2),
      pips: pips.toFixed(1),
    };
  }, [balance, entry, sl, risk, pair]);

  const riskLevel =
    risk[0] <= 1.5
      ? { tone: "safe" as const, label: "Safe", color: "var(--buy)", Icon: ShieldCheck, msg: "Conservative risk per trade." }
      : risk[0] <= 3
        ? { tone: "caution" as const, label: "Caution", color: "var(--warn)", Icon: ShieldAlert, msg: "Above recommended. Make sure setup is A+." }
        : { tone: "danger" as const, label: "Danger", color: "var(--sell)", Icon: AlertTriangle, msg: "Aggressive — a few losses can wreck the account." };

  return (
    <main className="flex-1 p-4 lg:p-6">
      <div className="mb-6">
        <h1 className="font-display text-2xl font-bold">Risk Calculator</h1>
        <p className="text-sm text-muted-foreground">
          Size your position before you click buy.
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="border-border/60 bg-card/60 p-6">
          <h2 className="mb-5 text-xs uppercase tracking-widest text-muted-foreground">Inputs</h2>
          <div className="space-y-5">
            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Pair</Label>
              <Select value={pair} onValueChange={handlePair}>
                <SelectTrigger className="mt-1.5 font-mono">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAIRS.map((p) => (
                    <SelectItem key={p} value={p} className="font-mono">
                      {p}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Account balance ($)</Label>
              <Input
                className="mt-1.5 font-mono"
                value={balance}
                onChange={(e) => setBalance(e.target.value)}
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Risk %</Label>
                <span className="font-mono text-sm text-primary">{risk[0].toFixed(2)}%</span>
              </div>
              <Slider min={0.5} max={5} step={0.1} value={risk} onValueChange={setRisk} className="mt-3" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Entry price</Label>
                <Input className="mt-1.5 font-mono" value={entry} onChange={(e) => setEntry(e.target.value)} />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Stop loss</Label>
                <Input className="mt-1.5 font-mono" value={sl} onChange={(e) => setSl(e.target.value)} />
              </div>
            </div>

            <div
              className="flex items-center gap-3 rounded-md border px-4 py-3"
              style={{
                borderColor: `color-mix(in oklab, ${riskLevel.color} 45%, transparent)`,
                backgroundColor: `color-mix(in oklab, ${riskLevel.color} 12%, transparent)`,
              }}
            >
              <riskLevel.Icon className="h-5 w-5" style={{ color: riskLevel.color }} />
              <div className="flex-1">
                <div className="text-xs font-bold uppercase tracking-widest" style={{ color: riskLevel.color }}>
                  Risk Level — {riskLevel.label}
                </div>
                <div className="text-xs text-muted-foreground">{riskLevel.msg}</div>
              </div>
            </div>
          </div>
        </Card>

        <Card className="relative overflow-hidden border-primary/40 bg-card/80 p-6 shadow-[var(--shadow-glow)]">
          <h2 className="mb-5 text-xs uppercase tracking-widest text-muted-foreground">Position Plan</h2>
          <div className="space-y-4">
            <Output label="Position size" value={`${result.lots} lots`} accent />
            <Output label="Dollar at risk" value={`$${result.dollarRisk}`} />
            <Output label="Pip risk" value={`${result.pips} pips`} />
            <Output label="Pip value (per pip move)" value={`$${result.pipValue}`} />
          </div>

          <div className="mt-6 rounded-md border border-border/60 bg-background/40 p-4 text-xs leading-relaxed text-muted-foreground">
            <div className="mb-1 font-bold uppercase tracking-widest text-foreground">Formula</div>
            <code className="block font-mono text-[11px] text-primary">
              Position size = (Balance × Risk%) ÷ (Pip Risk × Pip Value)
            </code>
            <p className="mt-2">
              Pip values assume standard lots (100,000 units). JPY pairs use 0.01 pip definition;
              gold uses 0.1. Confirm with your broker before trading.
            </p>
          </div>
        </Card>
      </div>
    </main>
  );
}

function Output({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className="flex items-baseline justify-between border-b border-border/40 pb-3">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className={`font-mono text-2xl font-bold ${accent ? "text-primary" : ""}`}>{value}</span>
    </div>
  );
}
